import { Controller } from '@hotwired/stimulus';
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';

/**
 * This controller handles the map's bounds and also the markers. On first render, it computes
 * the bounds that should get set in a somewhat complicated way (see the connect() method)
 * and also always fetches and renders the markers.
 *
 * Connects to data-controller="gmaps-bounds"
 */
export default class extends Controller {
  static values = {
    // The bounds value contains the geo coordinates of the edges that we currently show on the map.
    bounds: Object,
    // The next 3 values tell us how to pan to a circle with the radius being searched for, if
    // such a radius exists.
    radius: Number,
    latitude: Number,
    longitude: Number,
    // If no bounds value is given on initialization AND there are no marker targets, we fall back
    // to the viewport value to determine the section of the map we want to display. It is expected
    // to be a JSON string that can be passed to google.maps.LatLngBounds.
    viewport: String,
    // What's the path that we call to get the marker data (as JSON)?
    markersPath: String,
    // When we're on a landing page, this should contain the landing page's parameters. (They're
    // needed because we have to pass them to the markersPath endpoint.)
    additionalQueryString: String,
  };
  static targets = ['map'];

  async connect() {
    if (!this.hasMapTarget) {
      // This happens when the user didn't agree to use Google Maps.
      return;
    }

    this.clusterer = this.makeMarkerClusterer();
    const markers = await this.fetchAndClusterMarkers();

    this.clusteringEndListener = google.maps.event.addListener(
      this.clusterer,
      'clusteringend',
      () => this.dispatch('clusteringend'),
    );

    if (this.hasBoundsValue) {
      // Do nothing. If we got bounds from the server, we consider them correct
      // -- no need to do anything more. This happens when the user reloads the
      // search page after panning or zooming (since at that point the
      // gmaps_interaction controller has written the values into the URL); it
      // happens when the user arrives at the search page by clicking a link
      // containing a map_bounds parameter; finally, it happens when we change
      // filters while a region search is active (because when the
      // gmaps_interaction controller writes the map_bounds into the URL, it
      // also renders a Turbo stream which contains a hidden field which also
      // holds the map_bounds, and that field gets submitted with the filter
      // modification).
    } else if (
      this.hasRadiusValue &&
      this.hasLatitudeValue &&
      this.hasLongitudeValue
    ) {
      // If we didn't get *bounds* from the server, but we got a radius, we pan to the circle
      // that the gmaps-radius controller drew or is about to draw. (This doesn't mean we pan
      // twice, because, as that controller documents, it only fires its radius-changed event
      // when the radius changes *after* loading.)
      this.setBoundsForRadius(
        this.latitudeValue,
        this.longitudeValue,
        this.radiusValue,
      );
    } else if (markers.length) {
      // If we didn't get anything from the server, but we have markers, we find
      // the smallest region containing the markers and set the map's bounds to
      // that. This happens when the user switches from the list view (with more
      // than 0 projects) to the map view, it happens when the user is in the
      // map view without a map_bounds parameter (so, never having
      // zoomed/panned) and they change the filters or the location search, and
      // it also happens when the user clicks a link with mode=map but without a
      // map_bounds parameter.

      this.setBoundsForMarkers(markers);
    } else if (this.viewportValue.length) {
      // If we got nothing from the server and have no markers, but Google has
      // given us a viewport, we use that. This can happen when the user is in
      // the list view with a location search active, looking at 0 results, and
      // they switch to map mode.

      this.boundsValue = new google.maps.LatLngBounds(
        JSON.parse(this.viewportValue),
      );
    } else {
      // Finally, if absolutely nothing else worked, we fall back to all of
      // Germany. This can happen if the user, in list mode, sets filters to get
      // 0 results (but no location search) and then switches to map mode. It
      // can also happen in all situations described above "if (viewport)"
      // above, if we get no viewport from the Google API (which is documented
      // as possible, albeit unlikely). We use the most northern, eastern, etc.
      // points in Germany -- per Wikipedia.

      this.boundsValue = new google.maps.LatLngBounds(
        { lat: 47.270108, lng: 5.866667 },
        { lat: 54.9, lng: 15.033333 },
      );
    }

    // If the map's ready, render. (If the map isn't ready, the clusterer will automatically render
    // at the next 'idle' event, i. e. when it's ready.)
    if (
      this.mapTarget.innerMap?.getProjection() &&
      this.mapTarget.innerMap?.getBounds()
    ) {
      this.clusterer.render();
    }
  }

  disconnect() {
    this.clusterer?.setMap(null);
    this.clusteringEndListener?.remove();
  }

  boundsValueChanged(current) {
    // This method is called by Stimulus even if the user didn't agree to use Google Maps. In this
    // case, the controller doesn't have a map target.
    if (!this.hasMapTarget) {
      return;
    }

    if (Object.keys(current).length) {
      this.mapTarget.innerMap.fitBounds(current);
    }
  }

  // Important note: this does *not* actually render the markers! Call this.clusterer.render()
  // yourself.
  async fetchAndClusterMarkers() {
    const url = new URL(window.location.href);
    url.pathname = this.markersPathValue;

    new URLSearchParams(this.additionalQueryStringValue).forEach((v, k) =>
      url.searchParams.set(k, v),
    );

    const json = await fetch(url, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

    this.clusterer.clearMarkers(true);

    const markers = json.map(({ lat, lng, title, ids }) => {
      const content =
        ids.length === 1
          ? this.singleProjectMarker.cloneNode(true)
          : this.multiProjectMarker(ids.length);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        content,
      });

      marker.setAttribute('gmp-clickable', true);

      // To find the marker for project 42, we use the CSS selector
      // [project-ids*=" 42 "]. That's why we need the leading and trailing spaces.
      marker.setAttribute('project-ids', ` ${ids.join(' ')} `);

      // Google uses the title as accessibility feature.
      marker.setAttribute('title', title);

      marker.dataset.action = 'click->gmaps-interaction#onMarkerClick';
      marker.dataset.gmapsInfowindowProjectIdsParam = `${ids}`;

      return marker;
    });

    this.clusterer.addMarkers(markers, true);

    return markers;
  }

  async onRadiusChange(event) {
    const { radius, lat, lng } = event.detail;

    if (radius) {
      this.setBoundsForRadius(lat, lng, radius);
      await this.fetchAndClusterMarkers();
    } else {
      const markers = await this.fetchAndClusterMarkers();

      if (markers.length) {
        // The total absence of markers on a radius change to "exact borders" is
        // sort of a pathological case. As far as I can tell it'll only ever
        // happen if we were displaying *more* than the place's borders -- e. g.
        // we had a large radius and markers and went to just borders, or we had
        // no markers and had panned to the viewport. In either case doing
        // nothing ought to be fine, certainly better than calling fitBounds on
        // empty bounds (which moves us to someplace in the ocean).
        this.setBoundsForMarkers(markers);
      }
    }

    this.clusterer.render();
  }

  setBoundsForRadius(lat, lng, radius) {
    const bounds = new google.maps.LatLngBounds();

    const offset = (degrees) =>
      google.maps.geometry.spherical.computeOffset(
        { lat, lng },
        Number(radius) * 1000,
        degrees,
      );

    [0, 90, 180, 270].forEach((degrees) => bounds.extend(offset(degrees)));

    const north = bounds.getNorthEast().lat();
    const east = bounds.getNorthEast().lng();
    const south = bounds.getSouthWest().lat();
    const west = bounds.getSouthWest().lng();

    this.boundsValue = { north, east, south, west };
  }

  setBoundsForMarkers(markers) {
    const bounds = new google.maps.LatLngBounds();
    markers.forEach((marker) => bounds.extend(marker.position));
    this.boundsValue = bounds;
  }

  makeMarkerClusterer() {
    return new MarkerClusterer({
      map: this.mapTarget.innerMap,
      algorithm: new SuperClusterAlgorithm({ extent: 256 }),
      renderer: {
        render: ({ position, markers }, _stats, map) => {
          const projectIds = markers
            .map((marker) =>
              marker.getAttribute('project-ids').trim().split(' '),
            )
            .flat();

          const marker = new google.maps.marker.AdvancedMarkerElement({
            map,
            position,
            content: this.clusterMarker(projectIds.length),
          });

          marker.setAttribute('project-ids', ` ${projectIds.join(' ')} `);

          return marker;
        },
      },
    });
  }

  singleProjectSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 416.7 537.7" width="28" height="28">
      <path fill="var(--brand-primary-color)" d="M208.8,535.8Q411.6,301.7,411.6,197.3C411.6,93,320.8,8.4,208.8,8.4S6,93,6,197.3Q6,301.7,208.8,535.8Z"/>
      <ellipse fill="#fff" cx="208.8" cy="188.2" rx="83.5" ry="83.9"/>
    </svg>
  `;

  multiProjectSvg = (n) => `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 416.7 537.7" width="37" height="37">
      <path
        fill="var(--brand-primary-color)"
        d="M208.8,535.8Q411.6,301.7,411.6,197.3C411.6,93,320.8,8.4,208.8,8.4S6,93,6,197.3Q6,301.7,208.8,535.8Z"
      />
      <text
        x="50%"
        y="50%"
        dominant-baseline="middle"
        text-anchor="middle"
        font-size="14.5rem"
        fill="#fff"
        font-family="Raleway, sans-serif"
        font-weight="bold"
      >${n}</text>
    </svg>
  `;

  // Heads up: When you change the relation between viewBox and width/height, you need
  // to change font-size. If the viewBox is larger than the actual width/height,
  // font-size must be larger as well to end up with effectively 1rem.
  clusterSvg = (n) => `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 43 43"
      width="43"
      height="43"
    >
      <path
        fill="var(--brand-primary-color)"
        d="M38.7,33.5c-5.1,9.2-17.1,9.9-23.4,8.3C6.4,39.5.6,31.4.5,22.1.4,10.4,8.7,3.7,10.9,
          2.8s13.8-5.7,23.4,2c12.8,10.3,7.5,22.8,4.4,28.7"
      />
      <text
        x="50%"
        y="50%"
        dominant-baseline="middle"
        text-anchor="middle"
        font-size="1rem"
        fill="#fff"
        font-family="Raleway, sans-serif"
        font-weight="bold"
      >${n}</text>
    </svg>
  `;

  parser = new DOMParser();

  singleProjectMarker = this.parser.parseFromString(
    this.singleProjectSvg,
    'image/svg+xml',
  ).documentElement;

  multiProjectMarker = (n) =>
    this.parser.parseFromString(this.multiProjectSvg(n), 'image/svg+xml')
      .documentElement;

  clusterMarker = (n) =>
    this.parser.parseFromString(this.clusterSvg(n), 'image/svg+xml')
      .documentElement;
}
